import type { LineSeriesOption } from 'echarts';

import type { TimeRangeLane } from '@/shared/charts/time-series-chart/TimeSeriesChart.types';
import { getTimeRangeLaneColor } from '@/shared/charts/time-series-chart/theme/getTimeRangeLaneColor';
import type { TimeSeriesChartTheme } from '@/shared/charts/time-series-chart/theme/TimeSeriesChartTheme.types';

import { highlightVisuals } from '../config/chartVisuals';
import { LANE_HIGHLIGHT_SERIES_ID } from '../config/componentIds';
import { toTranslucentColor } from '../utils/toTranslucentColor';

type MarkAreaItemStyle = {
  color: string;
  borderColor: string;
  borderWidth: number;
  borderType: number[];
};

type MarkAreaPoint = { xAxis: number; itemStyle?: MarkAreaItemStyle };
type MarkAreaRange = [MarkAreaPoint, MarkAreaPoint];

function buildBandItemStyle(color: string): MarkAreaItemStyle {
  return {
    color: toTranslucentColor(color, highlightVisuals.fillOpacity),
    borderColor: color,
    borderWidth: highlightVisuals.borderWidth,
    borderType: [...highlightVisuals.bandBorderDashPattern],
  };
}

function buildLaneBands(lane: TimeRangeLane, theme: TimeSeriesChartTheme): MarkAreaRange[] {
  const itemStyle = buildBandItemStyle(getTimeRangeLaneColor(lane, theme));

  return lane.ranges.map(({ startTimestamp, endTimestamp }) => [
    { xAxis: Math.min(startTimestamp, endTimestamp), itemStyle },
    { xAxis: Math.max(startTimestamp, endTimestamp) },
  ]);
}

/**
 * One hidden carrier series in the plot; its markArea draws a full-height band for every range of
 * every highlighted lane. The series has no data of its own, it only exists to host the markArea.
 */
export function buildLaneHighlightOption(
  highlightedLanes: TimeRangeLane[],
  yAxisIndex: number,
  theme: TimeSeriesChartTheme
): LineSeriesOption {
  return {
    id: LANE_HIGHLIGHT_SERIES_ID,
    type: 'line',
    yAxisIndex,
    data: [],
    silent: true,
    lineStyle: {
      opacity: 0,
    },
    markArea: {
      silent: true,
      label: {
        show: false,
      },
      data: highlightedLanes.flatMap((lane) => buildLaneBands(lane, theme)),
    },
  };
}
